import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";

import { Option } from "components/Select/Select";
import { getItem, getProducts, PRODUCTS_PAGE_SIZE } from "features/Products/api";
import { Item } from "types";

export function useItemSearchOptions(search: string, selectedItemId?: string | number) {
  const trimmed = search.trim();
  const [debounced, setDebounced] = useState(trimmed);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(trimmed), 450);
    return () => clearTimeout(t);
  }, [trimmed]);

  const selectedId =
    selectedItemId !== undefined && selectedItemId !== null && selectedItemId !== ""
      ? Number(selectedItemId)
      : null;

  const selectedQuery = useQuery(
    ["item-selected", selectedId],
    () => getItem(selectedId as number),
    { enabled: typeof selectedId === "number" && !Number.isNaN(selectedId) }
  );

  const query = useQuery(
    ["item-search-options", debounced],
    () =>
      getProducts({ page: 1, pageSize: PRODUCTS_PAGE_SIZE, search: debounced }),
    {
      enabled: debounced.length > 0,
      keepPreviousData: true,
    }
  );

  const items: Item[] = useMemo(() => {
    const data = query.data ?? [];
    return Array.isArray(data) ? (data as Item[]) : [];
  }, [query.data]);

  const selectedItem = (selectedQuery.data ?? null) as Item | null;

  const options: Option[] = useMemo(() => {
    const base = items.map((i) => ({
      value: String(i.itemid),
      label: (i as any).longname || i.name,
    }));
    const selected = selectedItem
      ? ({
          value: String((selectedItem as any).itemid),
          label: (selectedItem as any).longname || (selectedItem as any).name,
        } as Option)
      : undefined;

    if (!selected) return base;

    const map = new Map<string, Option>();
    [selected, ...base].forEach((o) => map.set(o.value, o));
    return Array.from(map.values());
  }, [items, selectedQuery.data]);

  return {
    items: selectedItem
      ? ([selectedItem, ...items] as Item[])
      : items,
    options,
    isLoading:
      query.isLoading ||
      query.isFetching ||
      selectedQuery.isLoading ||
      selectedQuery.isFetching,
  };
}

